import { Button, SkeletonSpinnerCentered } from '@civilgrid/ui/components';
import { useParams } from '@tanstack/react-router';
import Dinero from 'dinero.js';
import { DateTime } from 'luxon';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { printReceipt as apiPrintReceipt } from '~/api/projects2';
import { getReasonForError } from '~/common/getReasonForError';
import { notifyExportQueued } from '~/common/notifyExportQueued';
import { capturePosthogEvent, captureTypedPosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import type { Transaction } from '~/common/types';
import { Dropdown } from '~/components2/Dropdown';
import { Notify } from '~/components2/Notify';
import { SendEmailModal } from '~/components2/SendEmailModal';
import { useGetProject } from '~/hooks/useGetProject';
import { useGetTransactions } from '~/hooks/useGetTransactions';
import SelectField from '~/pages/admin/common/SelectField';
import type { ProjectDetailsParams } from '..';

export function TabDetails() {
  const { projectId } = useParams({ strict: false }) as ProjectDetailsParams;
  const [isLoading, setIsLoading] = useState(false);
  const [printType, setPrintType] = useState<'docx' | 'pdf' | null>(null);
  const projectRequest = useGetProject({ projectId }, { refetchOnWindowFocus: false });
  const transactionsRequest = useGetTransactions({ projectId }, { refetchOnWindowFocus: false });

  const { control, watch } = useForm({
    defaultValues: {
      dateRange: 'All',
    },
  });

  const inputDateRange = watch('dateRange');

  // use first and last day of month if range selected, otherwise return undefined
  const parseDateRange = (selectedRange: string): { startDate: string; endDate: string } | undefined => {
    if (selectedRange === 'All') {
      return undefined;
    }

    const date = DateTime.fromFormat(selectedRange, 'MMMM yyyy');
    const startDate = date.startOf('month').toISO()!;
    const endDate = date.endOf('month').toISO()!;

    if (date.isValid) {
      return { startDate, endDate };
    }

    return undefined;
  };

  const printReceipt = async (
    type: 'docx' | 'pdf',
    dateRange: { startDate: string; endDate: string } | undefined,
    ccEmails: string[] | undefined,
  ) => {
    try {
      setIsLoading(true);
      const res = await apiPrintReceipt(projectId, type, dateRange, ccEmails);

      if (!res.success) {
        throw new Error('Error printing receipt');
      }

      notifyExportQueued({
        title: 'Print Receipt',
        message: 'Your transaction log request is being processed. You will receive the file via email shortly.',
        ccEmails,
      });

      capturePosthogEvent(POSTHOG_EVENTS.projectReceiptPrinted, {
        project_id: projectId,
        organization_id: projectRequest.data?.organization || undefined,
        format: type,
        start_date: dateRange?.startDate ?? '',
        end_date: dateRange?.endDate ?? '',
      });
    } catch (err) {
      captureTypedPosthogEvent(POSTHOG_EVENTS.projectReceiptPrintFailed, {
        project_id: projectId,
        organization_id: projectRequest.data?.organization || undefined,
        format: type,
        reason: getReasonForError(err),
      });
      Notify.error({
        title: 'Error Printing Receipt',
        message: 'Error printing receipt. Please try again or contact support.',
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const out = useMemo(() => {
    if (transactionsRequest.isLoading || !transactionsRequest.data) {
      return <SkeletonSpinnerCentered />;
    } else {
      const transactions: Transaction[] = transactionsRequest.data;

      const filteredAndSortedTransactions = transactions
        .filter((t) => {
          if (inputDateRange === 'All') {
            return true;
          }
          return t.createdAt.toFormat('MMMM yyyy') === inputDateRange;
        })
        .sort((a: Transaction, b: Transaction) => a.createdAt.toMillis() - b.createdAt.toMillis());

      const total = filteredAndSortedTransactions.reduceRight(
        (total, { amount }, i) => {
          const t = transactions[i];
          if (t.type === 'credit' || t.type === 'discount') {
            return total.subtract(amount);
          }

          return total.add(amount);
        },
        Dinero({ amount: 0 }),
      );

      const transactionMonthYears = transactions
        .sort((a, b) => a.createdAt.toMillis() - b.createdAt.toMillis())
        .map((t) => t.createdAt.toFormat('MMMM yyyy'));
      const uniqueMonthYearsArray = Array.from(new Set(transactionMonthYears));

      const shouldDisplayDisclaimer = projectRequest.data
        ? projectRequest.data.createdAt < DateTime.fromISO('2024-04-01')
        : false;

      return (
        <div className="flex h-full flex-col justify-between">
          <div className="flex w-full flex-col space-y-4">
            <div className="flex flex-row items-end justify-between gap-2">
              <SelectField
                id="dateRange"
                label="Filter Transactions By Month"
                control={control}
                items={[
                  { label: 'All', value: 'All' },
                  ...uniqueMonthYearsArray.map((item) => ({
                    label: item,
                    value: item,
                  })),
                ]}
              />
              <div className="">
                <Dropdown
                  side="bottom"
                  align="start"
                  triggerElement={<Button data-testid="project-billing-print-log-button">Print Transaction Log</Button>}
                  items={[
                    {
                      type: 'simple',
                      label: 'DOC (.docx)',
                      onClick: () => setPrintType('docx'),
                    },
                    {
                      type: 'simple',
                      label: 'PDF (.pdf)',
                      onClick: () => setPrintType('pdf'),
                    },
                  ]}
                />
              </div>
            </div>
            <div className="text-base font-semibold">Transaction Preview</div>

            <table className="relative w-full table-auto text-sm font-noto">
              <thead className="text-left">
                <tr>
                  <th>Date</th>
                  <th className="px-6">Description</th>
                  <th className="text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {filteredAndSortedTransactions.map((t, _i) => {
                  let a: string = `${t.amount.toFormat()}`;
                  const { type } = t;

                  if (type === 'credit' || type === 'discount') {
                    a = `(${a})`;
                  }

                  return (
                    <tr className="text-left" key={t.id}>
                      <td>{t.createdAt.toLocaleString(DateTime.DATE_SHORT)}</td>
                      <td className="px-6">{t.description}</td>
                      <td className="text-right">{a}</td>
                    </tr>
                  );
                })}
                <tr>
                  <td></td>
                  <td></td>
                  <td className="py-2">
                    <span className="w-ful block h-[1px] bg-secondary/20"></span>
                  </td>
                </tr>
                <tr>
                  <td></td>
                  <td className="pr-8 text-right font-bold">Total</td>
                  <td className="text-right font-bold">{total.toFormat()}</td>
                </tr>
              </tbody>
            </table>
          </div>
          {shouldDisplayDisclaimer && (
            <div className="self-baseline text-2xs">
              Note: Some transactions prior to April 1, 2024, may not display in this list. Please contact
              support@civilgrid.com or your account executive with any questions.
            </div>
          )}
        </div>
      );
    }
  }, [transactionsRequest.isLoading, inputDateRange, projectRequest.isLoading]);

  const printModal = (
    <SendEmailModal
      open={printType !== null}
      onOpenChange={(open) => {
        if (!open) setPrintType(null);
      }}
      title="Print Receipt"
      testIdPrefix="project-billing-print-log"
      onSend={(ccEmails) =>
        printType && printReceipt(printType, parseDateRange(inputDateRange), ccEmails.length > 0 ? ccEmails : undefined)
      }
    />
  );

  return (
    <>
      {printModal}
      {isLoading ? <SkeletonSpinnerCentered /> : out}
    </>
  );
}

export function MapChildren() {
  return null;
}
